import { useManager } from 'dreamstate';
import { useEffect } from 'react';

import { LiveErrorPage } from '@vimeo/live-core/components/error/page/LiveErrorPage';
import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { InteractionSessionManager } from '@vimeo/live-core/store/interaction/session';
import {
  T_ERROR_THE_LINK_MIGHT_HAVE_EXPIRED,
  T_ERROR_THIS_PAGE_ISNT_AVAILABLE,
} from '@vimeo/live-core/translations';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { createDomName } from '@vimeo/live-core/utils/dom/identifying';
import { LiveError } from '@vimeo/live-core/utils/error';
import { useIsVpaas } from '@vimeo/vpaas';

import { useInteractionToolsStatusObserverContext } from './components/embeding/InteractionToolsStatusObserver';
import {
  InteractionRoomLayout,
  InteractionLoginLayout,
  InteractionCollapsibleLoginLayout,
} from './components/layout';

/**
 * Int-x tools page that handles default flows of tools widget.
 */
export function InteractionToolsRouter({
  id = createDomName('loader'),
  className = createDomName('loader'),
  interactionSessionContext: {
    roomInfo: { value: roomInfo, isLoading: isRoomInfoLoading },
    roomUser: { value: roomUser, isLoading: isRoomUserLoading },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}) {
  const { initialState } = useLiveGlobals<IInteractionStoreInitialState>();
  const { setIsApplicationAvailableForModeration } = useInteractionToolsStatusObserverContext();
  const isVpaas: boolean = useIsVpaas();

  useEffect(() => {
    if (!isRoomInfoLoading && !isRoomUserLoading) {
      setIsApplicationAvailableForModeration(
        Boolean(roomUser?.isCreator && !initialState?.interaction?.feature?.isManagementDisabled),
      );
    }
  }, [
    initialState?.interaction?.feature?.isManagementDisabled,
    isRoomInfoLoading,
    isRoomUserLoading,
    roomUser?.isCreator,
    setIsApplicationAvailableForModeration,
  ]);

  if (isRoomInfoLoading) {
    return (
      <BlockingLoadingWrapper
        id={id}
        className={className}
        overlayColor={initialState?.interaction?.backgroundColor || 'background'}
        opacity={1}
        withLoader
      />
    );
  } else if (roomInfo) {
    if (roomUser) {
      return <InteractionRoomLayout />;
    } else {
      if (isVpaas) {
        return (
          <LiveErrorPage
            title={T_ERROR_THIS_PAGE_ISNT_AVAILABLE}
            message={T_ERROR_THE_LINK_MIGHT_HAVE_EXPIRED}
            isHeaderEnabled={false}
          />
        );
      } else if (
        initialState?.interaction?.auth?.isCollapsibleSignIn &&
        !initialState?.interaction?.isStandalone
      ) {
        return <InteractionCollapsibleLoginLayout />;
      } else {
        return <InteractionLoginLayout />;
      }
    }
  } else {
    throw new LiveError('Failed to render interaction application without room info.');
  }
}
